/**
 * BinCell - Individual bin display component with hover tooltip
 *
 * Displays a single bin with status color, time display, and stuck indicator.
 * Used in the BinGrid component for visual warehouse bin status monitoring.
 * Non-empty bins show a HoverCard tooltip with order details on hover.
 *
 * Design decisions:
 * - h-14 height (56px) for read-only status display:
 *   - Acceptable because this is a read-only display, not an interactive button
 *   - Interactive elements should use h-16+ (64px) per warehouse glove standards
 *   - TV Mode uses h-20 (80px) via className override for distance viewing
 * - Express bins keep the standard tile height; the badge uses its compact size so rows stay aligned
 * - Empty bins use bg-muted (subtle gray) for better visual hierarchy
 * - Stuck bins override to red background for urgent visibility
 * - Redo-pending bins override to amber-200 with left border accent (distinct from HELD)
 * - Time display is truncation-protected with px-1 padding
 * - Comprehensive aria-label includes stuck status for accessibility
 * - HoverCard only wraps non-EMPTY bins (TV mode: no mouse = no tooltip)
 */
import { useTranslation } from 'react-i18next';
import { RefreshCw, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { ExpressBadge } from './ExpressBadge';

type BinSize = 'STANDARD' | 'LARGE' | 'HUGE' | 'BENCH';

export interface BinCellProps {
  bin: {
    id: string;
    label: string;
    status: 'EMPTY' | 'IN_USE' | 'READY_TO_PACK' | 'HELD' | 'OCCUPIED' | 'READY_TO_RESUME' | 'CUSTOM_ISSUE';
    binSize?: string;            // STANDARD | LARGE | HUGE
    assignedAt: string | null;   // JSON serialized from Date
    isStuck: boolean;
    exceedsThreshold?: boolean;  // True if bin has been assigned longer than stuck threshold
    orderName?: string | null;   // NULLABLE per backend
    timeInBinMs?: number | null;
    isExpress?: boolean | null;
    hasRedoPending?: boolean;    // True if order has pending redo items
    itemsCaught?: number;
    totalItems?: number;
    redoCount?: number;
    redoBatchLabel?: string | null;
  };
  binSize?: BinSize;    // Grid-level size (controls height)
  compact?: boolean;    // For embedded vs TV mode
}

/**
 * Status color mapping following established design system.
 * Empty bins use bg-muted for subtle visual hierarchy.
 * Active states use semantic colors (orange=in progress, emerald=ready, amber=held).
 */
const BIN_STATUS_STYLES: Record<string, string> = {
  EMPTY: 'bg-muted text-muted-foreground',
  IN_USE: 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-200',
  OCCUPIED: 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-200',
  READY_TO_PACK: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200',
  READY_TO_RESUME: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200',
  HELD: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200',
  CUSTOM_ISSUE: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200',
};

/**
 * Redo-pending override: warm amber with left border accent.
 * Distinct from HELD (plain amber-100) — uses amber-200 base + amber-500 left border
 * to signal "needs attention / awaiting redo" vs "on hold".
 */
const REDO_PENDING_STYLE = 'bg-amber-200 text-amber-900 border-l-[3px] border-l-amber-500 dark:bg-amber-800/40 dark:text-amber-100 dark:border-l-amber-400';

/**
 * Stuck bin override: red background for urgent visibility.
 * Overrides all other status colors when bin exceeds stuck threshold.
 */
const STUCK_STYLE = 'bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200';

/** Progress bar fill colors matching bin status */
const PROGRESS_BAR_STYLES: Record<string, string> = {
  IN_USE: 'bg-orange-500',
  OCCUPIED: 'bg-orange-500',
  READY_TO_PACK: 'bg-emerald-500',
  READY_TO_RESUME: 'bg-emerald-500',
  HELD: 'bg-amber-500',
  CUSTOM_ISSUE: 'bg-amber-500',
};

/**
 * Format milliseconds to human-readable duration.
 * Handles days, hours, and minutes with compact format.
 *
 * @example formatDuration(3600000) => "1h 0m"
 * @example formatDuration(90000000) => "1d 1h"
 */
function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms < 0) return '';

  const minutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

/**
 * Tooltip content shown on hover for non-empty bins.
 * Shows order name, catch progress, redo status, and time in bin.
 */
function BinTooltipContent({ bin, timeDisplay }: {
  bin: BinCellProps['bin'];
  timeDisplay: string;
}) {
  const totalItems = bin.totalItems ?? 0;
  const itemsCaught = bin.itemsCaught ?? 0;
  const progressPercent = totalItems > 0 ? Math.round((itemsCaught / totalItems) * 100) : 0;
  // Suppress redo-pending styling when all items are caught (stale flag)
  const effectiveRedoPending = bin.hasRedoPending && totalItems > 0 && itemsCaught < totalItems;
  const barColor = effectiveRedoPending
    ? 'bg-amber-500'
    : PROGRESS_BAR_STYLES[bin.status] ?? 'bg-muted-foreground';

  return (
    <div className="w-56 p-3 space-y-2">
      {/* Header: Order name + bin label */}
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm truncate">
          {bin.orderName ?? 'No order'}
        </span>
        <span className="text-xs text-muted-foreground ml-2 shrink-0">
          {bin.label}
        </span>
      </div>

      {/* Progress bar + fraction */}
      {totalItems > 0 && (
        <div className="space-y-1">
          <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all', barColor)}
              style={{ width: `${Math.min(progressPercent, 100)}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {itemsCaught}/{totalItems} caught
          </p>
        </div>
      )}

      {/* Redo section — hidden when all items caught (stale flag) */}
      {effectiveRedoPending && (bin.redoCount ?? 0) > 0 && (
        <div className="text-xs text-amber-600 dark:text-amber-400 space-y-0.5">
          <div className="flex items-center gap-1">
            <RefreshCw className="h-3 w-3" />
            <span>Awaiting {bin.redoCount} redo{(bin.redoCount ?? 0) > 1 ? 's' : ''}</span>
          </div>
          {bin.redoBatchLabel && (
            <p className="pl-4 text-muted-foreground">
              In {bin.redoBatchLabel}
            </p>
          )}
        </div>
      )}

      {/* Time in bin */}
      {timeDisplay && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>{timeDisplay}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Extract display text from bin label.
 * Standard BIN prefix shows just the number.
 * OOS-001 -> O1, LRG-001 -> L1, HGE-001 -> H1
 * Non-standard prefixes abbreviate: "PACK-TEST-4" -> "PT4"
 */
function extractBinDisplay(label: string): string {
  // Match prefix (everything before last hyphen-digits)
  const match = label.match(/^(.+)-(\d+)$/);
  if (!match) return label;
  const prefix = match[1]!;
  const num = match[2]!.replace(/^0+/, '') || match[2]!;
  if (prefix === 'BIN') return num;
  if (prefix === 'OOS') return `O${num}`;
  if (prefix === 'LRG') return `L${num}`;
  if (prefix === 'HGE') return `H${num}`;
  // Abbreviate: first letter of each segment → "PACK-TEST" → "PT"
  const abbrev = prefix.split('-').map(s => s[0]?.toUpperCase() ?? '').join('');
  return `${abbrev}${num}`;
}

/** Height classes by bin size */
const BIN_HEIGHT: Record<BinSize, string> = {
  STANDARD: 'h-20',
  LARGE: 'h-24',
  HUGE: 'h-[96px]',
  // BLU-197: work benches share the HUGE tile height.
  BENCH: 'h-[96px]',
};

export function BinCell({ bin, binSize = 'STANDARD', compact = false }: BinCellProps) {
  const { t } = useTranslation('production');
  const showExpress = bin.status !== 'EMPTY' && bin.isExpress === true;
  const binNumber = extractBinDisplay(bin.label);
  const timeDisplay = formatDuration(bin.timeInBinMs);

  // Build comprehensive aria-label for screen readers
  // Use effectiveRedoPending for visual state (suppress when all caught)
  const totalItems = bin.totalItems ?? 0;
  const itemsCaught = bin.itemsCaught ?? 0;
  const effectiveRedoPending = bin.hasRedoPending && totalItems > 0 && itemsCaught < totalItems;
  const redoText = effectiveRedoPending ? ', redo pending' : '';
  const stuckText = bin.isStuck ? ', stuck' : '';
  const waitingLongText = bin.exceedsThreshold && (bin.status === 'READY_TO_PACK' || bin.status === 'READY_TO_RESUME') ? ', waiting a while' : '';
  const timeText = timeDisplay ? ` for ${timeDisplay}` : '';
  const statusText = bin.status.toLowerCase().replace(/_/g, ' ');
  const expressText = showExpress ? `, ${t('bins.expressLabel')}` : '';
  const ariaLabel = `Bin ${binNumber}, ${statusText}${timeText}${redoText}${stuckText}${waitingLongText}${expressText}`;

  const cellContent = (
    <div
      className={cn(
        'rounded-lg flex flex-col items-center justify-center relative select-none py-1.5',
        compact ? 'h-12' : BIN_HEIGHT[binSize],
        // Priority: stuck (red) > redo pending (amber) > status color
        bin.isStuck ? STUCK_STYLE
          : effectiveRedoPending ? REDO_PENDING_STYLE
          : BIN_STATUS_STYLES[bin.status],
      )}
      role="gridcell"
      aria-label={ariaLabel}
    >

      {/* Timer icon for READY_TO_PACK bins waiting a long time */}
      {bin.exceedsThreshold && (bin.status === 'READY_TO_PACK' || bin.status === 'READY_TO_RESUME') && (
        <Clock className="absolute top-1 right-1 h-3 w-3 text-emerald-700 dark:text-emerald-300 opacity-75" />
      )}

      {/* Bin number - large and bold */}
      <span className="text-xl font-bold leading-none">{binNumber}</span>

      {/* Time display - truncation protected */}
      {timeDisplay && bin.status !== 'EMPTY' && (
        <span className="text-[10px] opacity-75 mt-0.5 max-w-full truncate px-1">
          {timeDisplay}
        </span>
      )}

      {showExpress && <ExpressBadge size="sm" className="mt-1" />}

      {/* Order name for screen readers */}
      {bin.orderName && (
        <span className="sr-only">Order: {bin.orderName}</span>
      )}
    </div>
  );

  // Empty bins: no tooltip (nothing to show)
  if (bin.status === 'EMPTY') {
    return cellContent;
  }

  // Non-empty bins: wrap in HoverCard for details on hover
  return (
    <HoverCard openDelay={400}>
      <HoverCardTrigger asChild>
        {cellContent}
      </HoverCardTrigger>
      <HoverCardContent side="top" sideOffset={8} className="w-auto p-0">
        <BinTooltipContent bin={bin} timeDisplay={timeDisplay} />
      </HoverCardContent>
    </HoverCard>
  );
}
